import React from 'react';
import { Platform, TouchableWithoutFeedback, View } from 'react-native';
import NativeImagePicker from 'react-native-image-crop-picker';

type ImagePickerResult = {
  cancelled: boolean;
  uri?: string;
  width?: number;
  height?: number;
  error?: unknown;
};

type ImagePickerProps = {
  style?: any;
  settings?: Record<string, unknown>;
  children?: React.ReactNode;
  onComplete: (result: ImagePickerResult) => void;
  onFail?: (result: ImagePickerResult) => void;
};

const webStyles = {
  container: {
    display: 'flex',
    cursor: 'pointer'
  },
  input: {
    border: 0,
    width: 1,
    height: 1,
    opacity: 0,
    position: 'absolute' as const
  }
};

const notifyFailure = (onFail: ImagePickerProps['onFail'], error: unknown) => {
  onFail?.({ cancelled: false, error });
};

const readBrowserImage = (file: any, onComplete: ImagePickerProps['onComplete'], onFail: ImagePickerProps['onFail']) => {
  if (!file) return;

  if (!/^image\//.test(file.type || '')) {
    notifyFailure(onFail, new Error('Not an image.'));
    return;
  }

  const reader: any = new (globalThis as any).FileReader();

  reader.onload = (event: any) => {
    const uri = event.target?.result;
    if (typeof uri !== 'string') {
      notifyFailure(onFail, new Error('Unable to read the selected image.'));
      return;
    }

    const browserWindow: any = typeof window === 'undefined' ? undefined : window;
    if (!browserWindow?.Image) {
      onComplete({ cancelled: false, uri });
      return;
    }

    const image = new browserWindow.Image();
    image.onload = () => onComplete({ cancelled: false, uri, width: image.width, height: image.height });
    image.onerror = (error: unknown) => notifyFailure(onFail, error);
    image.src = uri;
  };

  reader.onerror = (error: unknown) => notifyFailure(onFail, error);
  reader.onabort = (error: unknown) => notifyFailure(onFail, error);
  reader.readAsDataURL(file);
};

const WebImagePicker = ({ style, onComplete, onFail, children }: ImagePickerProps) => (
  <label
    onChange={(event: any) => {
      const input = event.target;
      readBrowserImage(input?.files?.[0], onComplete, onFail);
      if (input) input.value = '';
    }}
    style={{ ...webStyles.container, ...style }}
  >
    <input style={webStyles.input} type="file" accept="image/*" aria-label="Choose image from gallery" />
    {children}
  </label>
);

const NativeImagePickerButton = ({ style, settings, onComplete, onFail, children }: ImagePickerProps) => {
  const pickImage = async () => {
    try {
      const selected = await NativeImagePicker.openPicker({
        mediaType: 'photo',
        multiple: false,
        ...(settings || {})
      });
      const image: any = Array.isArray(selected) ? selected[0] : selected;
      onComplete({
        cancelled: false,
        uri: image?.path,
        width: image?.width,
        height: image?.height
      });
    } catch (error) {
      if ((error as { code?: string })?.code === 'E_PICKER_CANCELLED') return;
      notifyFailure(onFail, error);
    }
  };

  return (
    <TouchableWithoutFeedback onPress={pickImage}>
      <View style={style}>{children}</View>
    </TouchableWithoutFeedback>
  );
};

const ImagePicker = (props: ImagePickerProps) =>
  Platform.OS === 'web' ? <WebImagePicker {...props} /> : <NativeImagePickerButton {...props} />;

export default ImagePicker;
